export type BlogSection =
  | { kind: "p"; text: string }
  | { kind: "h2"; text: string }
  | { kind: "h3"; text: string }
  | { kind: "ul"; items: string[] }
  | { kind: "ol"; items: string[] }
  | { kind: "code"; language: string; code: string }
  | { kind: "table"; headers: string[]; rows: string[][] };

export interface BlogArticle {
  slug: string;
  path: string;
  title: string;
  primaryKeyword: string;
  secondaryKeywords?: string[];
  volume: string;
  cpc: string;
  opportunity: string;
  description: string;
  readTime: string;
  updated: string;
  sections: BlogSection[];
}

export const blogArticles: BlogArticle[] = [
  {
    slug: "linkedin-posting-api",
    path: "/blog/linkedin-posting-api/",
    title: "How to Post on LinkedIn Using the API: Complete 2026 Guide",
    primaryKeyword: "linkedin posting api",
    volume: "140",
    cpc: "$18",
    readTime: "13 min read",
    updated: "July 2026",
    opportunity:
      "Current search results lean heavily on official docs and forum answers. This guide turns the pieces into one implementation path.",
    description:
      "A practical LinkedIn posting API guide covering OAuth, required permissions, profile versus company posts, media, scheduling, rate limits, and working examples.",
    sections: [
      { kind: "p", text: "The LinkedIn posting API is powerful, but most teams discover quickly that it is not a single copy-paste endpoint. You need the right product access, OAuth scopes, author URNs, media upload flow, version headers, and retry behavior before the first post goes live." },
      { kind: "p", text: "This guide shows the implementation shape for publishing LinkedIn posts from a SaaS product in 2026. SocialCast handles this flow behind one normalized publishing API, but the native workflow is useful to understand when debugging permissions or migration work." },
      { kind: "h2", text: "What you need before posting" },
      { kind: "ul", items: [
        "A LinkedIn developer app with access to the appropriate LinkedIn Marketing or sharing APIs.",
        "OAuth 2.0 authorization for the LinkedIn member or organization admin.",
        "The author URN for a personal profile or organization page.",
        "Write scopes such as member posting for personal posts and organization posting for company pages.",
        "A media upload step when publishing images or video.",
      ] },
      { kind: "h2", text: "Personal posts versus company page posts" },
      { kind: "table", headers: ["Use case", "Author", "Typical permission", "Notes"], rows: [
        ["Personal profile post", "urn:li:person:{id}", "Member social write access", "Best for user-authored content where the user is the visible author."],
        ["Company page post", "urn:li:organization:{id}", "Organization social write access", "Requires the authorizing user to have the right page role."],
        ["Sponsored or campaign content", "Ad account or campaign objects", "Marketing API access", "A different path from simple organic publishing."],
      ] },
      { kind: "h2", text: "OAuth and permissions" },
      { kind: "p", text: "Start with a standard OAuth authorization URL. Request only the scopes you need. In production, store refreshable credentials encrypted, keep the LinkedIn account connection tied to your internal workspace, and record which organization pages the user can publish to." },
      { kind: "code", language: "text", code: `https://www.linkedin.com/oauth/v2/authorization
  ?response_type=code
  &client_id=LINKEDIN_CLIENT_ID
  &redirect_uri=https://yourapp.com/oauth/linkedin/callback
  &scope=openid%20profile%20w_member_social%20w_organization_social` },
      { kind: "h2", text: "Create a text post" },
      { kind: "p", text: "For a basic post, send the author URN, lifecycle state, commentary, visibility, and distribution settings. Keep API version headers explicit so a future LinkedIn version change does not silently alter behavior." },
      { kind: "code", language: "javascript", code: `async function createLinkedInTextPost({ accessToken, authorUrn, text }) {
  const res = await fetch("https://api.linkedin.com/rest/posts", {
    method: "POST",
    headers: {
      Authorization: \`Bearer \${accessToken}\`,
      "Content-Type": "application/json",
      "LinkedIn-Version": "202506",
      "X-Restli-Protocol-Version": "2.0.0"
    },
    body: JSON.stringify({
      author: authorUrn,
      commentary: text,
      visibility: "PUBLIC",
      distribution: {
        feedDistribution: "MAIN_FEED",
        targetEntities: [],
        thirdPartyDistributionChannels: []
      },
      lifecycleState: "PUBLISHED"
    })
  });

  if (!res.ok) throw new Error(await res.text());
  return res.headers.get("x-restli-id") || res.json();
}` },
      { kind: "h2", text: "Images and video" },
      { kind: "ol", items: [
        "Initialize an upload for the LinkedIn owner.",
        "Upload the binary file to the upload URL returned by LinkedIn.",
        "Create the post referencing the uploaded media asset.",
        "Poll or listen for processing state when video is involved.",
      ] },
      { kind: "code", language: "javascript", code: `async function createLinkedInImagePost({ accessToken, authorUrn, imageAssetUrn, text }) {
  const res = await fetch("https://api.linkedin.com/rest/posts", {
    method: "POST",
    headers: {
      Authorization: \`Bearer \${accessToken}\`,
      "Content-Type": "application/json",
      "LinkedIn-Version": "202506",
      "X-Restli-Protocol-Version": "2.0.0"
    },
    body: JSON.stringify({
      author: authorUrn,
      commentary: text,
      visibility: "PUBLIC",
      distribution: { feedDistribution: "MAIN_FEED" },
      content: {
        media: {
          title: "Post image",
          id: imageAssetUrn
        }
      },
      lifecycleState: "PUBLISHED"
    })
  });

  if (!res.ok) throw new Error(await res.text());
  return res.headers.get("x-restli-id");
}` },
      { kind: "h2", text: "Scheduling LinkedIn posts" },
      { kind: "p", text: "LinkedIn publishing APIs are usually treated as immediate-publish endpoints. If your app offers scheduling, store a scheduled job in your own system, validate the token before publish time, and execute the API call when the post is due." },
      { kind: "code", language: "javascript", code: `await socialcast.posts.create({
  platforms: ["linkedin"],
  text: "New launch notes are live.",
  account_ids: ["ln_org_123"],
  publish_at: "2026-08-04T15:00:00Z"
});` },
      { kind: "h2", text: "Rate limits and retries" },
      { kind: "ul", items: [
        "Treat 429 responses as retryable and back off using the response headers when present.",
        "Do not retry duplicate publish requests blindly. Use idempotency keys in your own queue.",
        "Separate media upload retries from final publish retries.",
        "Log LinkedIn response bodies because permission and version errors are usually explicit.",
      ] },
      { kind: "h2", text: "How SocialCast simplifies this" },
      { kind: "p", text: "SocialCast stores connected LinkedIn accounts, normalizes profile and organization posting, schedules jobs, retries transient failures, and returns a single post status object. Your app sends one request; SocialCast handles LinkedIn-specific author URNs, media upload, and publish state tracking." },
    ],
  },
  {
    slug: "instagram-publishing-api",
    path: "/blog/instagram-publishing-api/",
    title: "Instagram Publishing API: How to Publish and Schedule Posts",
    primaryKeyword: "instagram publishing api",
    volume: "90",
    cpc: "$9.47",
    readTime: "12 min read",
    updated: "July 2026",
    opportunity:
      "The native Meta workflow is accurate but fragmented. A clearer implementation guide can win developers who need the whole flow in one place.",
    description:
      "Learn the Instagram Publishing API workflow for Business and Creator accounts, including Meta auth, media containers, Reels, carousels, limits, scheduling, and common errors.",
    sections: [
      { kind: "p", text: "The Instagram Publishing API lets approved apps publish images, videos, Reels, and carousels to eligible Instagram professional accounts. The catch is that publishing is a two-step container workflow, not a direct file upload endpoint." },
      { kind: "h2", text: "Supported account types" },
      { kind: "ul", items: [
        "Instagram Business accounts connected to a Facebook Page.",
        "Instagram Creator accounts where the selected Meta flow and permissions support publishing.",
        "Accounts authorized through a Meta app that has passed the required review for production use.",
      ] },
      { kind: "h2", text: "The native Meta workflow" },
      { kind: "ol", items: [
        "Authenticate the user with Meta OAuth.",
        "Find the Facebook Page and connected Instagram user ID.",
        "Create a media container with image_url or video_url.",
        "Wait until the container is ready when video processing is involved.",
        "Publish the container using the media_publish edge.",
      ] },
      { kind: "code", language: "javascript", code: `async function createInstagramImageContainer({ igUserId, accessToken, imageUrl, caption }) {
  const params = new URLSearchParams({
    image_url: imageUrl,
    caption,
    access_token: accessToken
  });

  const res = await fetch(
    \`https://graph.facebook.com/v23.0/\${igUserId}/media\`,
    { method: "POST", body: params }
  );

  if (!res.ok) throw new Error(await res.text());
  return res.json(); // { id: "creation_container_id" }
}` },
      { kind: "code", language: "javascript", code: `async function publishInstagramContainer({ igUserId, accessToken, creationId }) {
  const params = new URLSearchParams({
    creation_id: creationId,
    access_token: accessToken
  });

  const res = await fetch(
    \`https://graph.facebook.com/v23.0/\${igUserId}/media_publish\`,
    { method: "POST", body: params }
  );

  if (!res.ok) throw new Error(await res.text());
  return res.json(); // { id: "instagram_media_id" }
}` },
      { kind: "h2", text: "Images, Reels, and carousels" },
      { kind: "table", headers: ["Format", "Native workflow", "Important detail"], rows: [
        ["Single image", "Create one media container, then publish it.", "Use a publicly reachable image URL."],
        ["Reel", "Create a REELS container with a video URL, wait for processing, then publish.", "Video readiness and format issues are common failure points."],
        ["Carousel", "Create child containers, create a carousel parent, then publish.", "Instagram carousels support up to 10 items."],
      ] },
      { kind: "h2", text: "Publishing limits" },
      { kind: "p", text: "Meta documents a moving 24-hour publishing cap for API-published Instagram posts, and carousels count as a single post. Apps should track account-level usage, warn before the limit is hit, and avoid retry loops that accidentally consume attempts." },
      { kind: "h2", text: "Scheduling Instagram posts" },
      { kind: "p", text: "For SaaS scheduling, the durable approach is to store the planned post, validate media early, refresh credentials before publish time, and run the container/publish workflow at the scheduled timestamp." },
      { kind: "code", language: "javascript", code: `await socialcast.posts.create({
  platforms: ["instagram"],
  text: "Behind the scenes from launch week.",
  media: [{ type: "image", url: "https://cdn.example.com/launch.jpg" }],
  account_ids: ["ig_17841400000000000"],
  publish_at: "2026-08-12T18:30:00Z"
});` },
      { kind: "h2", text: "Common errors" },
      { kind: "ul", items: [
        "The Instagram account is not a supported professional account.",
        "The Facebook Page and Instagram user are not connected as expected.",
        "The image or video URL is not public, expires too soon, or returns the wrong content type.",
        "A video container is not ready when media_publish is called.",
        "The account has hit a publishing limit or the app lacks approved permissions.",
      ] },
      { kind: "h2", text: "How SocialCast removes the Meta workflow complexity" },
      { kind: "p", text: "SocialCast turns Meta's native workflow into one API call. It manages OAuth, account discovery, media validation, container creation, video readiness checks, carousel assembly, scheduling, retries, and final publish status. Your product does not need to build a separate Meta job runner before shipping Instagram publishing." },
    ],
  },
  {
    slug: "social-media-posting-api",
    path: "/blog/social-media-posting-api/",
    title: "Social Media Posting API: Post and Schedule Across Multiple Platforms",
    primaryKeyword: "social media posting api",
    secondaryKeywords: ["social media scheduling api", "social media scheduler api", "unified social media api"],
    volume: "40 + 70 secondary",
    cpc: "$15-$22",
    readTime: "15 min read",
    updated: "July 2026",
    opportunity:
      "Posting and scheduling searches overlap heavily, so this pillar covers the full unified API workflow instead of splitting thin articles.",
    description:
      "A pillar guide to unified social media posting APIs: one request, platform normalization, scheduling, media uploads, retries, webhooks, analytics, and AI-agent workflows.",
    sections: [
      { kind: "p", text: "A social media posting API lets your product create, schedule, and monitor posts across networks without forcing your team to build every native integration from scratch. The value is not just a POST endpoint. The value is normalizing OAuth, media rules, scheduling, retries, status, analytics, and platform-specific edge cases." },
      { kind: "h2", text: "What a unified social media API should handle" },
      { kind: "ul", items: [
        "One post creation model across LinkedIn, Instagram, Facebook, X, TikTok, YouTube, Reddit, Pinterest, Telegram, and other networks.",
        "Per-platform account connection and OAuth refresh.",
        "Media upload, validation, transcoding, and platform-specific formatting.",
        "Scheduling with timezone-safe timestamps.",
        "Retries, idempotency, and failure state tracking.",
        "Webhooks for post lifecycle events.",
        "Analytics and post history retrieval.",
      ] },
      { kind: "h2", text: "One API call" },
      { kind: "code", language: "javascript", code: `const post = await socialcast.posts.create({
  text: "We just shipped our new release.",
  platforms: ["linkedin", "instagram", "facebook"],
  account_ids: ["ln_org_123", "ig_1784", "fb_page_456"],
  media: [
    { type: "image", url: "https://cdn.example.com/release-cover.jpg" }
  ],
  publish_at: "2026-08-20T14:00:00Z",
  idempotency_key: "release-2026-08-20"
});` },
      { kind: "h2", text: "Platform normalization" },
      { kind: "table", headers: ["Problem", "Native reality", "Unified API behavior"], rows: [
        ["Text length", "Each network has different limits.", "Validate and return per-platform warnings before publish."],
        ["Media", "Different upload and processing flows.", "Accept one media object and prepare platform-specific assets."],
        ["Identity", "Profiles, pages, channels, and organizations all differ.", "Expose connected accounts as a consistent account_id."],
        ["Status", "Networks return different success and error payloads.", "Normalize into queued, publishing, published, failed, or canceled."],
      ] },
      { kind: "h2", text: "Scheduling and retries" },
      { kind: "p", text: "A scheduling API should store the planned publish time, validate the post before it is due, and run a durable job at publish time. Use idempotency keys so retrying a job never creates duplicate posts. Separate transient failures from permanent failures like missing permissions or rejected media." },
      { kind: "h2", text: "Media uploads" },
      { kind: "p", text: "The cleanest developer experience is to let callers provide either a hosted URL or a direct upload. The API should return validation errors early: file too large, unsupported codec, missing alt text, wrong aspect ratio, or a platform-specific constraint." },
      { kind: "h2", text: "Webhooks" },
      { kind: "code", language: "json", code: `{
  "event": "post.published",
  "post_id": "pst_01J...",
  "platform": "linkedin",
  "account_id": "ln_org_123",
  "social_post_id": "urn:li:share:...",
  "published_at": "2026-08-20T14:00:04Z"
}` },
      { kind: "h2", text: "Analytics" },
      { kind: "p", text: "A strong unified social media API should connect publish state with analytics state. That lets your product show reach, engagement, comments, clicks, and per-platform performance without building separate reporting pipelines for every network." },
      { kind: "h2", text: "AI-agent usage" },
      { kind: "p", text: "AI agents need guardrails more than they need raw posting credentials. A unified API can expose approved accounts, draft-only mode, policy checks, scheduled publishing, and webhooks back to the agent runtime. The agent proposes content; your system approves and publishes through a controlled API." },
      { kind: "h2", text: "Why SocialCast" },
      { kind: "p", text: "SocialCast is built for SaaS products, agencies, internal tools, and AI-agent workflows that need one publishing layer instead of a pile of native integrations. Connect accounts once, call one API, and monitor every platform from the same post object." },
    ],
  },
  {
    slug: "postiz-alternatives",
    path: "/blog/postiz-alternatives/",
    title: "Best Postiz Alternatives for Developers and Agencies in 2026",
    primaryKeyword: "postiz alternative",
    volume: "30",
    cpc: "$18.47",
    readTime: "11 min read",
    updated: "July 2026",
    opportunity:
      "Commercial intent is strong and current results include mixed forum, review, and vendor pages. A transparent developer comparison can stand out.",
    description:
      "Compare SocialCast and Postiz across API access, hosted versus self-hosted operation, networks, scheduling, automation, pricing, and maintenance.",
    sections: [
      { kind: "p", text: "Postiz is a strong open-source social media scheduling product, especially for teams that want self-hosting and a visible product UI. But developer and agency requirements often point in a different direction: hosted infrastructure, account management, one API, webhooks, analytics, and less maintenance." },
      { kind: "h2", text: "Short answer" },
      { kind: "p", text: "Choose Postiz if you want an open-source scheduling app you can self-host and customize. Choose SocialCast if you want a hosted social media posting API for SaaS products, agencies, and AI-agent workflows where the integration layer matters more than running your own scheduler." },
      { kind: "h2", text: "Feature matrix" },
      { kind: "table", headers: ["Category", "SocialCast", "Postiz"], rows: [
        ["Primary fit", "Unified posting API for products and agencies", "Open-source scheduling app with automation features"],
        ["Hosting", "Hosted API and managed infrastructure", "Self-hosted or cloud options"],
        ["API access", "Core product surface", "Public API available"],
        ["OAuth management", "Managed account connection layer", "Depends on deployment and provider setup"],
        ["Scheduling", "API-first scheduling and status tracking", "Scheduling UI and API workflows"],
        ["Automation", "Designed for AI agents and backend workflows", "Supports automation tools and AI workflows"],
        ["Maintenance", "No server upkeep for customers", "Self-hosters manage deploys, env vars, updates, and provider keys"],
      ] },
      { kind: "h2", text: "API access" },
      { kind: "p", text: "For developers, the key question is whether the API is the product or an extension of the product. SocialCast is designed around API-first publishing, scheduling, media handling, webhooks, and analytics. Postiz exposes a public API, but many teams still evaluate it first as a scheduling application." },
      { kind: "h2", text: "Hosted versus self-hosted" },
      { kind: "p", text: "Self-hosting is attractive when you need control, internal customization, or open-source deployment. It also means you own upgrades, secrets, provider app configuration, uptime, backups, and incident response. A hosted API is better when social publishing is infrastructure your product depends on but does not need to operate." },
      { kind: "h2", text: "Supported networks and maintenance" },
      { kind: "p", text: "Network coverage changes often because platforms change OAuth rules, media rules, and posting APIs. When comparing any Postiz alternative, look beyond the logo grid. Ask who owns the provider maintenance when LinkedIn changes headers, Instagram changes media rules, or X requires a different media upload flow." },
      { kind: "h2", text: "Pricing and operations" },
      { kind: "p", text: "Open-source software can reduce license costs, but agencies and SaaS teams should price the operator time too: hosting, monitoring, upgrades, provider credentials, app review, user support, and security reviews. SocialCast prices for managed API usage so teams can budget around publishing volume and account needs." },
      { kind: "h2", text: "When SocialCast is the better Postiz alternative" },
      { kind: "ul", items: [
        "You need to embed publishing into your SaaS product.",
        "You need one API for multiple customer workspaces.",
        "You want managed OAuth and connected account infrastructure.",
        "You need webhooks, retries, and analytics in your backend.",
        "You are building AI agents that need controlled publish access.",
      ] },
      { kind: "h2", text: "Migration checklist" },
      { kind: "ol", items: [
        "List connected networks and account owners.",
        "Export scheduled posts and historical content if available.",
        "Reconnect accounts in SocialCast with the right workspace mapping.",
        "Map each Postiz integration to a SocialCast account_id.",
        "Recreate scheduled posts through the SocialCast API.",
        "Test webhooks and failure handling before switching production traffic.",
      ] },
    ],
  },
  {
    slug: "ayrshare-alternatives",
    path: "/blog/ayrshare-alternatives/",
    title: "Best Ayrshare Alternatives for Social Media API Publishing",
    primaryKeyword: "ayrshare alternative",
    volume: "30",
    cpc: "$18.28",
    readTime: "12 min read",
    updated: "July 2026",
    opportunity:
      "Developers searching alternatives are usually comparing API design, account limits, pricing, webhooks, analytics, and fit for product infrastructure.",
    description:
      "A developer-focused Ayrshare alternative comparison covering API design, account limits, pricing, platform coverage, OAuth, webhooks, analytics, AI agents, and migration.",
    sections: [
      { kind: "p", text: "Ayrshare is a mature social media API with broad platform support. If you are searching for an Ayrshare alternative, the goal is probably not just finding another publish endpoint. You are comparing developer experience, account modeling, OAuth ownership, webhooks, analytics, pricing, and how well the API fits your product." },
      { kind: "h2", text: "What developers should compare" },
      { kind: "ul", items: [
        "API object model: posts, accounts, workspaces, media, schedules, webhooks, and analytics.",
        "Account limits and how multi-customer SaaS workspaces are represented.",
        "OAuth management and whether customer accounts connect without manual token handling.",
        "Platform coverage for the networks your users actually need.",
        "Failure handling, retries, and idempotency.",
        "Webhook coverage for scheduled, published, failed, and account events.",
        "Pricing model as your connected accounts and post volume grow.",
        "AI-agent support, permissions, and approval flows.",
      ] },
      { kind: "h2", text: "Transparent feature matrix" },
      { kind: "table", headers: ["Category", "SocialCast", "Ayrshare"], rows: [
        ["Primary fit", "API-first publishing for SaaS, agencies, and AI agents", "Unified social API for broad social media workflows"],
        ["Posting and scheduling", "One normalized post object with scheduling", "Post endpoint supports publishing and scheduling options"],
        ["OAuth", "Managed connected accounts per workspace", "Managed social account connection model"],
        ["Webhooks", "Post lifecycle and account events", "Webhook support for async events"],
        ["Analytics", "Publishing status and performance data", "Analytics and history APIs"],
        ["AI agents", "Designed for controlled agent publishing workflows", "Offers API infrastructure that can be used by agents"],
        ["Migration posture", "Developer-led migration with account mapping", "Existing API customers should compare endpoint and object mapping"],
      ] },
      { kind: "h2", text: "API design" },
      { kind: "p", text: "A good Ayrshare alternative should feel predictable in code. Your backend should create a post, attach accounts, schedule it, receive webhooks, and fetch results without maintaining a separate mental model for every social network." },
      { kind: "code", language: "javascript", code: `await socialcast.posts.create({
  text: "Customer story is live.",
  platforms: ["linkedin", "facebook", "instagram"],
  account_ids: ["ln_org_123", "fb_page_456", "ig_1784"],
  media: [{ type: "image", url: "https://cdn.example.com/story.jpg" }],
  publish_at: "2026-09-01T16:00:00Z"
});` },
      { kind: "h2", text: "OAuth and account limits" },
      { kind: "p", text: "For SaaS builders, account modeling matters as much as platform coverage. You need to know whether accounts belong to your customer, your workspace, your agency client, or your internal team. SocialCast keeps account connections scoped to workspaces and exposes stable account IDs to your application." },
      { kind: "h2", text: "Webhooks and analytics" },
      { kind: "p", text: "Publishing is asynchronous. A useful API sends events when a post is queued, published, failed, retried, canceled, or updated with platform IDs. Analytics then turns the platform result into product-visible metrics." },
      { kind: "h2", text: "Migration instructions" },
      { kind: "ol", items: [
        "Inventory every Ayrshare profile, connected account, and active scheduled post.",
        "Map those profiles to SocialCast workspaces.",
        "Reconnect social accounts through SocialCast OAuth.",
        "Translate publish requests to SocialCast post objects.",
        "Register webhook endpoints and replay test events in staging.",
        "Run both systems in parallel for a small set of accounts.",
        "Move production scheduling once status and analytics match your expectations.",
      ] },
      { kind: "h2", text: "When to choose SocialCast" },
      { kind: "p", text: "Choose SocialCast when you want a focused, API-first publishing layer for your app, agency operations, or AI-agent workflows. It is built to normalize posting, scheduling, media, retries, webhooks, and analytics while keeping native platform complexity out of your product code." },
    ],
  },
];

export function getBlogArticle(path: string) {
  const normalized = normalizeBlogPath(path);
  return blogArticles.find((article) => normalizeBlogPath(article.path) === normalized);
}

function normalizeBlogPath(path: string) {
  const [base] = path.split("?");
  return base.endsWith("/") ? base : `${base}/`;
}

